import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { format } from 'date-fns'

import { Badge } from '@gto/ui/badge'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { TruncatedText } from '@gto/ui/truncated-text'

import type { EarningHistory } from '@/api/gen'
import { fixedColumn, fluidColumn, headerWithHint } from '@/components/table-columns'
import { EdgeTiersTooltip } from '@/pages/rewards/components/edge-tiers-tooltip'

export const useEarningsHistoryTableCols = (): ColumnDef<EarningHistory>[] => {
  const { formatMessage, formatNumber } = useIntl()

  /**
   * The deal's basis is highlighted and the other greyed, so a row shows at a glance which of
   * GGR/NGR it was actually paid on.
   */
  const rakeCell = (value: number, isBasis: boolean) => (
    <span className={isBasis ? 'font-semibold text-primary-main' : 'text-text-ghost'}>
      <FormattedNumber
        currency="USD"
        style="currency"
        value={value}
      />
    </span>
  )

  return createColumns<EarningHistory>([
    {
      accessorKey: 'year',
      header: formatMessage({ id: 'rewards.earnings.table.year' }),
      ...fixedColumn({ min: 75, align: 'center', headerAlign: 'center' }),
    },
    {
      accessorKey: 'month',
      header: formatMessage({ id: 'rewards.earnings.table.month' }),
      ...fixedColumn({ min: 100, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => format(new Date(row.original.year, row.original.month - 1, 1), 'MMMM'),
    },
    {
      accessorKey: 'poker_room',
      header: formatMessage({ id: 'rewards.earnings.table.pokerRoomName' }),
      ...fluidColumn({ min: 220 }),
      // Wrapped onto a second line under DataGridPro's auto row height; DS cells never wrap, so
      // a long room name truncates with the full text in the tooltip it already carried.
      //
      // The category dot that used to sit here read `last_processed_category` through a cast to
      // `EarningDeal` — a field `EarningHistory` does not have, so it was always undefined and
      // the badge never rendered. Dropped with the cast.
      cell: ({ row }) => (
        <TruncatedText
          placement="right"
          variant="body2"
        >
          {row.original.poker_room}
        </TruncatedText>
      ),
    },
    {
      accessorKey: 'rake_ggr',
      header: formatMessage({ id: 'rewards.earnings.table.ggr' }),
      ...fluidColumn({ min: 120, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => rakeCell(Number(row.original.rake_ggr), row.original.deal_type === 'GGR'),
    },
    {
      accessorKey: 'rake_ngr',
      header: formatMessage({ id: 'rewards.earnings.table.ngr' }),
      ...fluidColumn({ min: 140, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => rakeCell(Number(row.original.rake_ngr), row.original.deal_type === 'NGR'),
    },
    {
      accessorKey: 'edge_multiplier',
      header: headerWithHint(
        formatMessage({ id: 'rewards.earnings.table.edgeMultiplier' }),
        <FormattedMessage id="rewards.earnings.table.edgeMultiplier.tip" />,
      ),
      ...fluidColumn({ min: 160, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => {
        const { current_tier, edge_multiplier, tier_structure } = row.original

        return (
          <div className="flex items-center gap-2">
            <Badge color="info">{`x ${formatNumber(Number(edge_multiplier))}`}</Badge>
            {tier_structure !== null && tier_structure !== undefined && (
              <EdgeTiersTooltip
                current_tier={current_tier}
                tier_structure={tier_structure}
              />
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'edge_credited',
      header: headerWithHint(
        formatMessage({ id: 'rewards.earnings.table.edgeCredited' }),
        <FormattedMessage id="rewards.earnings.table.edgeCredited.tip" />,
      ),
      ...fluidColumn({ min: 160, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => <FormattedNumber value={Number(row.original.edge_credited)} />,
    },
  ])
}
